import { useEffect, useState } from "react";
import Field from "./Field";
import {
    INQUIRY_COUNTRIES_ISO,
    OTHER_COUNTRY_VALUE,
} from "../../../data/formOptions";
import {
    loadGeo,
    getStatesOfCountrySync,
    getCitiesOfStateSync,
    getCountryByCodeSync,
} from "../../../data/geoLoader";

/**
 * Cascading Country → State → City selector.
 *
 * • Country: curated select of ~25 markets ACT actively serves, plus "Other"
 *   sentinel that swaps state & city to free-text inputs.
 * • State:   dropdown populated from country-state-city if the selected
 *   country has states; input hidden entirely if the country has no
 *   subdivision data (e.g. Monaco, Singapore).
 * • City:    dropdown populated from country-state-city if the selected
 *   state has cities; falls back to a free-text input otherwise.
 *
 * All three write to `form.location_country`, `form.location_state`,
 * `form.location_city` (ISO codes for country + state; display string for
 * city). The parent hook resolves ISO → display name at submit time.
 */
export default function LocationFields({
    form,
    errors,
    setField,
    selectCls,
    inputCls,
}) {
    const [geoReady, setGeoReady] = useState(false);

    useEffect(() => {
        let cancelled = false;
        loadGeo().then(() => {
            if (!cancelled) setGeoReady(true);
        });
        return () => {
            cancelled = true;
        };
    }, []);

    const isOther = form.location_country === OTHER_COUNTRY_VALUE;

    // Curated country list, sorted by our editorial ordering (US first).
    const countryList = geoReady
        ? INQUIRY_COUNTRIES_ISO.map(getCountryByCodeSync).filter(Boolean)
        : [];

    const stateList =
        geoReady && form.location_country && !isOther
            ? getStatesOfCountrySync(form.location_country)
            : [];

    const cityList =
        geoReady && form.location_country && form.location_state && !isOther
            ? getCitiesOfStateSync(
                  form.location_country,
                  form.location_state,
              )
            : [];

    const hasStates = !isOther && stateList.length > 0;
    const hasCities = !isOther && cityList.length > 0;

    return (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-7">
            {/* Country */}
            <Field
                label="Country"
                htmlFor="i_country"
                required
                error={errors.location_country}
            >
                <select
                    id="i_country"
                    value={form.location_country}
                    onChange={(e) =>
                        setField("location_country", e.target.value)
                    }
                    className={selectCls}
                    disabled={!geoReady}
                    data-testid="inquiry-location_country"
                >
                    <option value="">
                        {geoReady ? "Select…" : "Loading regions…"}
                    </option>
                    {countryList.map((c) => (
                        <option key={c.isoCode} value={c.isoCode}>
                            {c.name}
                        </option>
                    ))}
                    <option value={OTHER_COUNTRY_VALUE}>Other</option>
                </select>
            </Field>

            {/* State / Region */}
            <Field
                label={isOther ? "Region / Province" : "State / Region"}
                htmlFor="i_state"
                required={isOther || hasStates}
                error={errors.location_state}
                hint={
                    !isOther && !hasStates && form.location_country
                        ? "Not applicable"
                        : undefined
                }
            >
                {isOther ? (
                    <input
                        id="i_state"
                        type="text"
                        value={form.location_state}
                        onChange={(e) =>
                            setField("location_state", e.target.value)
                        }
                        className={inputCls}
                        data-testid="inquiry-location_state"
                    />
                ) : (
                    <select
                        id="i_state"
                        value={form.location_state}
                        onChange={(e) =>
                            setField("location_state", e.target.value)
                        }
                        className={selectCls}
                        disabled={
                            !form.location_country || stateList.length === 0
                        }
                        data-testid="inquiry-location_state"
                    >
                        <option value="">
                            {!form.location_country
                                ? "Select country first…"
                                : stateList.length === 0
                                  ? "Not applicable"
                                  : "Select…"}
                        </option>
                        {stateList.map((s) => (
                            <option key={s.isoCode} value={s.isoCode}>
                                {s.name}
                            </option>
                        ))}
                    </select>
                )}
            </Field>

            {/* City */}
            <Field
                label="City"
                htmlFor="i_city"
                required={hasCities}
                error={errors.location_city}
                hint={!hasCities ? "Optional" : undefined}
            >
                {isOther || !hasCities ? (
                    <input
                        id="i_city"
                        type="text"
                        value={form.location_city}
                        onChange={(e) =>
                            setField("location_city", e.target.value)
                        }
                        className={inputCls}
                        data-testid="inquiry-location_city"
                    />
                ) : (
                    <select
                        id="i_city"
                        value={form.location_city}
                        onChange={(e) =>
                            setField("location_city", e.target.value)
                        }
                        className={selectCls}
                        disabled={!form.location_state}
                        data-testid="inquiry-location_city"
                    >
                        <option value="">
                            {!form.location_state
                                ? "Select state first…"
                                : "Select…"}
                        </option>
                        {cityList.map((c) => (
                            <option
                                key={`${c.name}-${c.latitude}-${c.longitude}`}
                                value={c.name}
                            >
                                {c.name}
                            </option>
                        ))}
                    </select>
                )}
            </Field>
        </div>
    );
}
